import { useState } from 'react';
import { Box, Stack, Group, Badge, Table, TextInput, Select, ActionIcon, Menu, Avatar, Progress } from '@mantine/core';
import {
  IconSearch,
  IconPlus,
  IconDotsVertical,
  IconEye,
  IconEdit,
  IconTrash,
} from '@tabler/icons-react';
import { Card, Text, Title, Button } from '../../../../components/ui';
import { mockProjects, mockUsers } from '../../mockData';

const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(date);
};

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
};

export default function ProjectsPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [priorityFilter, setPriorityFilter] = useState<string | null>(null);

  const statuses = ['planning', 'in-progress', 'on-hold', 'completed', 'cancelled'];
  const priorities = ['low', 'medium', 'high', 'urgent'];

  const filteredProjects = mockProjects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(search.toLowerCase()) ||
                          project.description.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = !statusFilter || project.status === statusFilter;
    const matchesPriority = !priorityFilter || project.priority === priorityFilter;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'in-progress': return 'blue';
      case 'planning': return 'cyan';
      case 'on-hold': return 'yellow';
      case 'completed': return 'green';
      case 'cancelled': return 'red';
      default: return 'gray';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'red';
      case 'high': return 'orange';
      case 'medium': return 'yellow';
      case 'low': return 'gray';
      default: return 'gray';
    }
  };

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>Projects</Title>
          <Text c="dimmed" size="sm">{filteredProjects.length} projects found</Text>
        </Box>
        <Button leftSection={<IconPlus size={16} />}>New Project</Button>
      </Group>

      {/* Filters */}
      <Card>
        <Group>
          <TextInput
            placeholder="Search projects..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Status"
            data={statuses.map(s => ({ value: s, label: s.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') }))}
            value={statusFilter}
            onChange={setStatusFilter}
            clearable
            w={150}
          />
          <Select
            placeholder="Priority"
            data={priorities.map(p => ({ value: p, label: p.charAt(0).toUpperCase() + p.slice(1) }))}
            value={priorityFilter}
            onChange={setPriorityFilter}
            clearable
            w={130}
          />
        </Group>
      </Card>

      {/* Projects Table */}
      <Card p={0}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Project</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Priority</Table.Th>
              <Table.Th>Progress</Table.Th>
              <Table.Th>Team</Table.Th>
              <Table.Th>Due Date</Table.Th>
              <Table.Th ta="right">Budget</Table.Th>
              <Table.Th w={50}></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filteredProjects.map((project) => {
              const owner = mockUsers.find(u => u.id === project.ownerId);
              const members = project.memberIds.slice(0, 3).map(id => mockUsers.find(u => u.id === id));

              return (
                <Table.Tr key={project.id}>
                  <Table.Td>
                    <Box>
                      <Text size="sm" fw={500}>{project.name}</Text>
                      <Text size="xs" c="dimmed" lineClamp={1}>{project.category}</Text>
                    </Box>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" color={getStatusColor(project.status)}>
                      {project.status.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" variant="outline" color={getPriorityColor(project.priority)}>
                      {project.priority}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Progress value={project.progress} size="sm" radius="xl" w={80} />
                      <Text size="xs">{project.progress}%</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Avatar.Group spacing="sm">
                      {members.map((member, i) => (
                        <Avatar key={i} size="sm" radius="xl" color="blue">
                          {member?.name.split(' ').map(n => n[0]).join('')}
                        </Avatar>
                      ))}
                      {project.memberIds.length > 3 && (
                        <Avatar size="sm" radius="xl" color="gray">
                          +{project.memberIds.length - 3}
                        </Avatar>
                      )}
                    </Avatar.Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{formatDate(project.dueDate)}</Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm" fw={500}>
                      {project.budget ? formatCurrency(project.budget) : '-'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Menu position="bottom-end">
                      <Menu.Target>
                        <ActionIcon variant="subtle" color="gray">
                          <IconDotsVertical size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item leftSection={<IconEye size={14} />}>View Details</Menu.Item>
                        <Menu.Item leftSection={<IconEdit size={14} />}>Edit</Menu.Item>
                        <Menu.Divider />
                        <Menu.Item leftSection={<IconTrash size={14} />} color="red">Delete</Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Card>
    </Stack>
  );
}
